import { enqueue } from './http';
import { ops, type QueryArguments, type QueryOptions, type MutationArguments, type MutationInput, type SubscriptionArguments, type SubscriptionInput } from './ops';
import { ws_subscribe } from './ws';
import type { subscription_root, subscription_rootRequest } from '../generated/schema';

/**
 * High-level typed facade over the transport layer.
 *
 * Each method builds an OpRequest via `ops.*`, sends it through the batched
 * HTTP client (or the shared websocket for subscriptions), and unwraps the
 * result to the raw payload the caller expects (row, array, count, etc.).
 *
 * This is what `create_store` configs use to talk to Hasura:
 *
 *   fetch: {
 *     many: (opts) => graphql.query_many('shipments_routes', opts?.where ? { where: opts.where } : undefined, ROUTE_FIELDS),
 *     one:  (id)   => graphql.query_one('shipments_routes_by_pk', { id }, ROUTE_FIELDS),
 *     ...
 *   }
 *
 * The return type is intentionally `unknown` — the concrete row/array shape
 * is pinned by the `return_fields` argument at each call site (callers cast
 * to their typed row as needed).
 */
export const graphql = {
	query_one<T extends keyof import('../generated/schema').query_rootRequest>(
		collection: T,
		input: QueryOptions<T>,
		return_fields: QueryArguments<T>
	): Promise<unknown | null> {
		return enqueue(ops.query_one(collection, input, return_fields));
	},

	query_many<T extends keyof import('../generated/schema').query_rootRequest>(
		collection: T,
		options: QueryOptions<T> | undefined,
		return_fields: QueryArguments<T>
	): Promise<unknown[] | null> {
		return enqueue(ops.query_many(collection, options, return_fields));
	},

	mutate<T extends keyof import('../generated/schema').mutation_rootRequest>(
		collection: T,
		input: MutationInput<T>,
		return_fields: MutationArguments<T>
	): Promise<unknown | null> {
		return enqueue(ops.mutate(collection, input, return_fields));
	},

	delete_one<T extends keyof import('../generated/schema').mutation_rootRequest>(
		collection: T,
		id: string
	): Promise<{ id: string } | null> {
		return enqueue(ops.delete_one(collection, id));
	},

	subscribe<T extends keyof subscription_rootRequest>(
		collection: T,
		input: SubscriptionInput<T>,
		return_fields: SubscriptionArguments<T>,
		on_next: (rows: subscription_root[T & keyof subscription_root]) => void,
		on_error?: (error: unknown) => void
	): () => void {
		const op = ops.subscribe(collection, input, return_fields);
		return ws_subscribe(op, {
			next: (result) => {
				const payload = result?.data?.[collection];
				if (payload != null) {
					on_next(payload as subscription_root[T & keyof subscription_root]);
				}
			},
			error: on_error
		});
	}
};